import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Divider,
  styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchPremiumGrantHistory } from '../../../services/userServices';
import { format } from 'date-fns';

const GRANT_TYPES = {
  SINGLE: 'single',
  BULK: 'bulk'
};

// Styled component for header cells
const HeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  '& .MuiTableCell-root': {
    color: theme.palette.primary.contrastText
  }
}));

const PremiumGrantHistoryModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    total_count: 0,
    total_bulk_granted_users: 0,
    limit: 100,
    offset: 0
  });
  const [metadata, setMetadata] = useState({
    can_view_all: true
  });
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [summaryData, setSummaryData] = useState({
    single_grants: 0,
    bulk_grants: 0,
  });

  // Load summary data on first open
  useEffect(() => {
    if (open) {
      loadSummaryData();
    }
  }, [open]);

  // Load tab data when tab, page, or rows per page changes
  useEffect(() => {
    if (open) {
      loadTabData();
    }
  }, [open, currentTab, page, rowsPerPage]);

  const loadSummaryData = async () => {
    try {
      // Load single grants count (includes both individual and bulk-generated records)
      const singleResponse = await fetchPremiumGrantHistory({
        limit: 1,
        offset: 0,
        grantType: GRANT_TYPES.SINGLE
      });

      // Load bulk operations count and bulk-granted users count
      const bulkResponse = await fetchPremiumGrantHistory({
        limit: 1,
        offset: 0,
        grantType: GRANT_TYPES.BULK
      });

      const totalSingleRecords = singleResponse.data.pagination.total_count;

      setSummaryData({
        single_grants: totalSingleRecords,
        bulk_grants: bulkResponse.data.metrics.total_bulk_granted_users,
      });
    } catch (error) {
      console.error('Error loading summary data:', error);
    }
  };

  const loadTabData = async () => {
    setLoading(true);
    try {
      const response = await fetchPremiumGrantHistory({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        grantType: currentTab === 0 ? GRANT_TYPES.SINGLE : GRANT_TYPES.BULK
      });

      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || {
        total_count: 0,
        total_bulk_granted_users: 0,
        limit: rowsPerPage,
        offset: page * rowsPerPage
      });
      setMetadata(response.data.metadata || { can_view_all: true });
    } catch (error) {
      console.error('Error loading tab data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0); // Reset page when switching tabs
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        Premium Grant History
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading && !summaryData.single_grants ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={currentTab} onChange={handleTabChange}>
                <Tab label={`Single Grants (${summaryData.single_grants})`} />
                <Tab label={`Bulk Operations (${summaryData.bulk_grants})`} />
              </Tabs>
            </Box>

            <TableContainer component={Paper}>
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : currentTab === 0 ? (
                <Table>
                  <TableHead>
                    <TableRow>
                      <HeaderCell>Date</HeaderCell>
                      <HeaderCell>User</HeaderCell>
                      <HeaderCell>Email</HeaderCell>
                      <HeaderCell>Duration</HeaderCell>
                      <HeaderCell>Reason</HeaderCell>
                      <HeaderCell>Granted By</HeaderCell>
                      <HeaderCell>Status</HeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((grant) => (
                      <TableRow key={grant.id}>
                        <TableCell>{format(new Date(grant.timestamp), 'MMM d, yyyy HH:mm')}</TableCell>
                        <TableCell>{grant.user_name}</TableCell>
                        <TableCell>{grant.user_email}</TableCell>
                        <TableCell>{grant.duration}</TableCell>
                        <TableCell>{grant.reason || '-'}</TableCell>
                        <TableCell>{grant.granted_by}</TableCell>
                        <TableCell>{grant.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <HeaderCell>Date</HeaderCell>
                      <HeaderCell>Admin</HeaderCell>
                      <HeaderCell>Created</HeaderCell>
                      <HeaderCell>Granted</HeaderCell>
                      <HeaderCell>Errors</HeaderCell>
                      <HeaderCell>Status</HeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((grant) => (
                      <TableRow key={grant.id}>
                        <TableCell>{format(new Date(grant.timestamp), 'MMM d, yyyy HH:mm')}</TableCell>
                        <TableCell>{grant.admin_name}</TableCell>
                        <TableCell>{grant.created_count}</TableCell>
                        <TableCell>{grant.granted_count}</TableCell>
                        <TableCell>{grant.error_count}</TableCell>
                        <TableCell>{grant.status}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <TablePagination
                component="div"
                count={pagination.total_count}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </TableContainer>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PremiumGrantHistoryModal; 